import type { WsEvent } from '@chatbot/shared-types';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { conversationsApi } from '../lib/conversations-api.ts';
import { useAuth } from './useAuth.tsx';
import { useWebSocket } from './useWebSocket.ts';

export type WsStatus = 'connecting' | 'open' | 'closed' | 'degraded';

interface WsContextValue {
  status: WsStatus;
  subscribe: (cb: (event: WsEvent) => void) => () => void;
  pendingCounts: Record<string, number>;
  lastReconnectAt: number | null;
}

const WebSocketContext = createContext<WsContextValue | null>(null);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { token, user } = useAuth();
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});

  // Centralize WebSocket instantiation
  const { status, lastReconnectAt, subscribe } = useWebSocket({
    token,
    enabled: !!token,
  });

  // Eager initialization of pending counts for authorized tenants
  useEffect(() => {
    if (!token || !user?.tenantScope || user.tenantScope.length === 0) {
      setPendingCounts({});
      return;
    }

    let isMounted = true;

    async function initPendingCounts() {
      try {
        const scopes = user?.tenantScope ?? [];
        const promises = scopes.map((tenantId) =>
          conversationsApi
            .list({ tenantId, state: 'pending_human' })
            .then((res) => ({ tenantId, count: res.items.length }))
            .catch((err) => {
              console.error(`Failed to eager-fetch pending counts for tenant ${tenantId}:`, err);
              return { tenantId, count: 0 };
            }),
        );

        const results = await Promise.all(promises);
        if (isMounted) {
          const counts: Record<string, number> = {};
          for (const { tenantId, count } of results) {
            counts[tenantId] = count;
          }
          setPendingCounts(counts);
        }
      } catch (err) {
        console.error('Error in eager initialization of pending counts:', err);
      }
    }

    initPendingCounts();

    return () => {
      isMounted = false;
    };
  }, [token, user?.tenantScope]);

  // Subscribe to live events to update pending counts reactively
  useEffect(() => {
    if (!token) return;

    const unsubscribe = subscribe((event: WsEvent) => {
      if (event.type === 'conversation.escalated') {
        setPendingCounts((prev) => ({
          ...prev,
          [event.tenantId]: (prev[event.tenantId] ?? 0) + 1,
        }));
      } else if (event.type === 'state.changed') {
        const { fromState, toState, tenantId } = event;
        if (fromState !== 'pending_human' && toState === 'pending_human') {
          setPendingCounts((prev) => ({
            ...prev,
            [tenantId]: (prev[tenantId] ?? 0) + 1,
          }));
        } else if (fromState === 'pending_human' && toState !== 'pending_human') {
          setPendingCounts((prev) => ({
            ...prev,
            [tenantId]: Math.max(0, (prev[tenantId] ?? 0) - 1),
          }));
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [token, subscribe]);

  return (
    <WebSocketContext.Provider value={{ status, subscribe, pendingCounts, lastReconnectAt }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useGlobalWebSocket(): WsContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useGlobalWebSocket must be used within a WebSocketProvider');
  }
  return ctx;
}
